import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../api/client';

export type AuthUser = {
  userId: string;
  name: string;
  role: 'admin' | 'technician';
  tenantId: string;
  mustChangePassword?: boolean;
};

const normalizeRole = (role: unknown): AuthUser['role'] => {
  const value = typeof role === 'string' ? role.trim().toLowerCase() : '';
  return value === 'admin' ? 'admin' : 'technician';
};

const normalizeUser = (user: any): AuthUser => ({
  ...user,
  role: normalizeRole(user?.role),
  mustChangePassword: Boolean(user?.mustChangePassword)
});

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<AuthUser | null>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const res = await apiRequest<{ user: AuthUser }>('/api/auth/me');
      const normalized = res.user ? normalizeUser(res.user) : null;
      setUser(normalized);
      return normalized;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    refreshSession().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshSession]);

  const login = async (email: string, password: string) => {
    const res = await apiRequest<{ user: AuthUser }>('/api/auth/login', {
      method: 'POST',
      json: { email, password }
    });
    const normalized = normalizeUser(res.user);
    setUser(normalized);
    return normalized;
  };

  const logout = async () => {
    setUser(null);
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } catch {
      // Session already cleared locally; cookie clear is best-effort
    }
  };

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshSession }),
    [user, loading, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
